/**
 * PaymentMilestonesSection Component
 * Displays payment milestones with cumulative progress timeline for won projects
 */

import React, { useState, useEffect } from 'react';
import { paymentMilestoneAPI, paymentNoticeAPI } from '../../services/api';
import { toast } from 'react-toastify';
import PaymentProgressTimeline from './PaymentProgressTimeline';
import RecordPaymentModal from './RecordPaymentModal';
import AddMilestoneModal from './AddMilestoneModal';
import EditMilestoneModal from './EditMilestoneModal';
import EditPaymentModal from './EditPaymentModal';
import CreateSiteEntryModal from './CreateSiteEntryModal';
import VOSection from './VOSection';
import DeductionsSection from './DeductionsSection';
import PaymentAuditTrail from './PaymentAuditTrail';
import IssuePaymentNoticeModal from './IssuePaymentNoticeModal';

const PaymentMilestonesSection = ({ project, onSiteEntryCreated }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Modal states
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showEditMilestone, setShowEditMilestone] = useState(false);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [showIssueNotice, setShowIssueNotice] = useState(false);
  const [noticeMilestone, setNoticeMilestone] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    if (project?.id && project?.workflowStatus === 'project_closed_won') {
      fetchData();
    }
  }, [project?.id, project?.workflowStatus]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Summary now includes milestones with cumulative data
      const summaryRes = await paymentMilestoneAPI.getSummary(project.id);
      setSummary(summaryRes.data.data || null);
    } catch (err) {
      console.error('Error fetching payment milestones:', err);
      // If no milestones exist yet, try to initialize them
      if (err.response?.status === 404 || (err.data?.count === 0)) {
        try {
          await paymentMilestoneAPI.initialize(project.id);
          // Retry fetch after initialization
          const summaryRes = await paymentMilestoneAPI.getSummary(project.id);
          setSummary(summaryRes.data.data || null);
        } catch (initErr) {
          setError('Failed to initialize payment milestones');
        }
      } else {
        setError('Failed to load payment milestones');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle recording a payment
  const handleRecordPayment = (milestone) => {
    setSelectedMilestone(milestone);
    setShowRecordPayment(true);
  };

  const handlePaymentRecorded = async () => {
    setShowRecordPayment(false);
    setSelectedMilestone(null);
    await fetchData();
    toast.success('Payment recorded successfully');
  };

  // Handle adding a milestone
  const handleMilestoneAdded = async () => {
    setShowAddMilestone(false);
    await fetchData();
    toast.success('Milestone added successfully');
  };

  // Handle editing a milestone
  const handleEditMilestone = (milestone) => {
    setSelectedMilestone(milestone);
    setShowEditMilestone(true);
  };

  const handleMilestoneUpdated = async () => {
    setShowEditMilestone(false);
    setSelectedMilestone(null);
    await fetchData();
  };

  // Handle editing a payment
  const handleEditPayment = (payment, milestone) => {
    setSelectedPayment(payment);
    setSelectedMilestone(milestone);
    setShowEditPayment(true);
  };

  const handlePaymentUpdated = async () => {
    setShowEditPayment(false);
    setSelectedPayment(null);
    setSelectedMilestone(null);
    await fetchData();
  };

  // Handle payment notice flow
  const handleIssueNotice = (milestone) => {
    setNoticeMilestone(milestone);
    setShowIssueNotice(true);
  };

  const handleNoticeIssued = async () => {
    setShowIssueNotice(false);
    setNoticeMilestone(null);
    await fetchData();
    toast.success('Payment notice issued');
  };

  const handleCancelNotice = async (milestone, noticeId) => {
    const reason = window.prompt('Reason for cancelling this notice? (optional)') ?? '';
    try {
      await paymentNoticeAPI.cancel(project.id, milestone.id, noticeId, reason || 'Cancelled');
      toast.success('Notice cancelled');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel notice');
    }
  };

  // Handle site entry creation
  const handleSiteEntryCreated = async () => {
    setShowCreateSite(false);
    await fetchData();
    toast.success('Site entry created successfully');
    if (onSiteEntryCreated) {
      onSiteEntryCreated();
    }
  };

  // Don't show for non-won projects
  if (project?.workflowStatus !== 'project_closed_won') {
    return null;
  }

  // Calculate progress percentage
  const progressPercentage = summary?.totalPercentagePaid || 0;
  const nextMilestone = summary?.nextMilestone;
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header - Always visible */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Payment Milestones</h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAddMilestone(true);
            }}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 min-h-[44px] px-2 touch-manipulation"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden xs:inline">Add</span>
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium focus:outline-none min-h-[44px] px-2 -mr-2 touch-manipulation flex-shrink-0"
          >
            <span className="hidden xs:inline">{isExpanded ? 'Less' : 'More'}</span>
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Preview Content - Always visible */}
      <div className="px-4 sm:px-6 pb-4">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="text-center py-4">
            <p className="text-red-600 text-sm mb-2">{error}</p>
            <button
              onClick={fetchData}
              className="text-sm text-blue-600 hover:text-blue-700 min-h-[44px] touch-manipulation"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Summary Preview */}
        {!loading && !error && summary && (
          <div className="space-y-3">
            {/* Progress bar with percentage */}
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">Payment Progress</span>
                <span className={`font-semibold ${progressPercentage >= 100 ? 'text-green-600' : progressPercentage >= 50 ? 'text-blue-600' : 'text-orange-600'}`}>
                  {progressPercentage.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${progressPercentage >= 100 ? 'bg-green-500' : 'bg-gradient-to-r from-green-400 to-green-500'}`}
                  style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Quick stats row */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-gray-500">Collected: </span>
                  <span className="font-medium text-green-600">{formatCurrency(summary.totalPaidAmount)}</span>
                </div>
                <div className="hidden sm:block">
                  <span className="text-gray-500">Remaining: </span>
                  <span className="font-medium text-orange-600">{formatCurrency(summary.remainingAmount)}</span>
                </div>
              </div>
              {summary.siteEntryCreated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Site Created
                </span>
              )}
            </div>

            {/* Next milestone quick action - only when not expanded */}
            {!isExpanded && nextMilestone && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-blue-600 font-medium uppercase">Next: {nextMilestone.milestoneName}</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(nextMilestone.paymentDue)} due</p>
                </div>
                <button
                  onClick={() => handleRecordPayment(nextMilestone)}
                  className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:scale-[0.98] min-h-[40px] touch-manipulation transition-all whitespace-nowrap"
                >
                  Record
                </button>
              </div>
            )}

            {/* All complete badge - only when not expanded */}
            {!isExpanded && !nextMilestone && summary.milestones?.length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-lg">
                <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-green-700 font-medium">All payments complete</span>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && !summary && (
          <div className="text-center py-4 text-gray-500">
            <p className="text-sm">No payment milestones found.</p>
            <button
              onClick={() => setShowAddMilestone(true)}
              className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium min-h-[44px] touch-manipulation"
            >
              Add your first milestone
            </button>
          </div>
        )}
      </div>

      {/* Expanded Content - Collapsible */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {!loading && !error && summary && (
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-2 border-t border-gray-100">
            {/* Active notice indicator (project-level) */}
            {summary.hasActiveNotice && (
              <div className="mb-4 p-3 rounded-lg bg-purple-50 border border-purple-200 flex items-start gap-2">
                <svg className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-purple-900">Payment notice active</p>
                  <p className="text-xs text-purple-700">
                    {(summary.milestones || [])
                      .filter(m => m.activeNotice)
                      .map(m => m.milestoneName)
                      .join(', ')} — awaiting client payment
                  </p>
                </div>
              </div>
            )}

            {/* Payment Progress Timeline */}
            <PaymentProgressTimeline
              summary={summary}
              milestones={summary.milestones || []}
              onRecordPayment={handleRecordPayment}
              onEditMilestone={handleEditMilestone}
              onEditPayment={handleEditPayment}
              onIssueNotice={handleIssueNotice}
              onCancelNotice={handleCancelNotice}
            />

            {/* Create Site Entry Button */}
            {!summary.siteEntryCreated && (
              <div className="mt-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-medium text-blue-900">Ready to Start Site Work?</h3>
                    <p className="text-sm text-blue-700">
                      {progressPercentage >= 50
                        ? `${progressPercentage.toFixed(1)}% collected - you can create the site entry now.`
                        : `${progressPercentage.toFixed(1)}% collected - typically 50% is required before starting site work.`}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCreateSite(true)}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-[0.98] font-medium text-sm whitespace-nowrap min-h-[44px] touch-manipulation transition-all"
                  >
                    Create Site Entry
                  </button>
                </div>
              </div>
            )}

            {/* Site Entry Created Badge */}
            {summary.siteEntryCreated && (
              <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-green-700 font-medium">Site entry has been created</span>
              </div>
            )}

            {/* Variation Orders Section */}
            <VOSection
              projectId={project.id}
              summary={summary}
              onRefresh={fetchData}
            />

            {/* Deductions (Refunds + Late Penalty Interest) */}
            <DeductionsSection
              projectId={project.id}
              summary={summary}
              onRefresh={fetchData}
            />

            {/* Payment Audit Trail */}
            <PaymentAuditTrail projectId={project.id} />
          </div>
        )}
      </div>

      {/* Modals */}
      {showRecordPayment && selectedMilestone && (
        <RecordPaymentModal
          isOpen={showRecordPayment}
          onClose={() => {
            setShowRecordPayment(false);
            setSelectedMilestone(null);
          }}
          onSuccess={handlePaymentRecorded}
          milestone={selectedMilestone}
          projectId={project.id}
        />
      )}

      {showAddMilestone && (
        <AddMilestoneModal
          isOpen={showAddMilestone}
          onClose={() => setShowAddMilestone(false)}
          onSuccess={handleMilestoneAdded}
          projectId={project.id}
          packagePrice={summary?.totalPackagePrice || project.currentPackagePrice}
        />
      )}

      {showEditMilestone && selectedMilestone && (
        <EditMilestoneModal
          isOpen={showEditMilestone}
          onClose={() => {
            setShowEditMilestone(false);
            setSelectedMilestone(null);
          }}
          onSuccess={handleMilestoneUpdated}
          milestone={selectedMilestone}
          projectId={project.id}
          packagePrice={summary?.totalPackagePrice || project.currentPackagePrice}
        />
      )}

      {showEditPayment && selectedPayment && selectedMilestone && (
        <EditPaymentModal
          isOpen={showEditPayment}
          onClose={() => {
            setShowEditPayment(false);
            setSelectedPayment(null);
            setSelectedMilestone(null);
          }}
          onSuccess={handlePaymentUpdated}
          payment={selectedPayment}
          milestone={selectedMilestone}
          projectId={project.id}
          summary={summary}
        />
      )}

      {showCreateSite && (
        <CreateSiteEntryModal
          isOpen={showCreateSite}
          onClose={() => setShowCreateSite(false)}
          onSuccess={handleSiteEntryCreated}
          project={project}
          summary={summary}
        />
      )}

      {showIssueNotice && noticeMilestone && (
        <IssuePaymentNoticeModal
          isOpen={showIssueNotice}
          onClose={() => {
            setShowIssueNotice(false);
            setNoticeMilestone(null);
          }}
          onSuccess={handleNoticeIssued}
          milestone={noticeMilestone}
          projectId={project.id}
        />
      )}
    </div>
  );
};

export default PaymentMilestonesSection;
